import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	type CreateShortlinkInput,
	type ShortlinksFilter,
	type UpdateShortlinkInput,
	shortLinksApi,
} from "./short-links-api";

export const KEY_SHORT_LINKS = ["short-links"] as const;

export function useShortLinksList(filter: ShortlinksFilter = {}) {
	return useQuery({
		queryKey: [...KEY_SHORT_LINKS, "list", filter],
		queryFn: () => shortLinksApi.list(filter),
	});
}

export function useShortLink(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_SHORT_LINKS, "one", id],
		queryFn: () => shortLinksApi.get(id as string),
		enabled: Boolean(id),
	});
}

export function useShortLinkStats(id: string | undefined, days = 30) {
	return useQuery({
		queryKey: [...KEY_SHORT_LINKS, "stats", id, days],
		queryFn: () => shortLinksApi.stats(id as string, days),
		enabled: Boolean(id),
	});
}

export function useShortLinksOverview(days = 30) {
	return useQuery({
		queryKey: [...KEY_SHORT_LINKS, "overview", days],
		queryFn: () => shortLinksApi.overview(days),
	});
}

export function useShortLinkClicks(
	id: string | undefined,
	page = 1,
	pageSize = 50,
	includeBots = false,
) {
	return useQuery({
		queryKey: [...KEY_SHORT_LINKS, "clicks", id, page, pageSize, includeBots],
		queryFn: () => shortLinksApi.clicks(id as string, page, pageSize, includeBots),
		enabled: Boolean(id),
	});
}

/**
 * État du raccourcisseur.
 *
 * Rafraîchi toutes les deux minutes : la file de clics et le retard
 * d'agrégation évoluent lentement, et cette requête tourne sur toutes les pages
 * de la section.
 */
export function useShortLinkHealth() {
	return useQuery({
		queryKey: [...KEY_SHORT_LINKS, "health"],
		queryFn: () => shortLinksApi.health(),
		refetchInterval: 120_000,
		// Un incident de supervision ne doit pas faire clignoter une erreur sur
		// une page qui, elle, fonctionne.
		retry: false,
	});
}

export function useCreateShortLink() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateShortlinkInput) => shortLinksApi.create(data),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_SHORT_LINKS });
		},
	});
}

export function useUpdateShortLink() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateShortlinkInput }) =>
			shortLinksApi.update(id, data),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_SHORT_LINKS });
		},
	});
}

export function useArchiveShortLink() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => shortLinksApi.archive(id),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_SHORT_LINKS });
		},
	});
}

export function useInvalidateShortLinkCache() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => shortLinksApi.invalidate(id),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_SHORT_LINKS });
		},
	});
}
